import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Home, Users, ChevronRight, Plus, Upload, Trash2, Loader2, X, Download, Building2, Sparkles,
  CheckCircle2, AlertTriangle, ShieldCheck, ShieldAlert, FileText, Check
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { GstLookup, panFromGstin } from '../components/GstLookup';
import SearchableSelect from '../components/SearchableSelect';

const REQUIRED_KYC_DOCS = [
  // 1. Statutory & Tax Registrations
  {
    key: 'IEC',
    label: 'IEC',
    title: 'IEC (Import Export Code)',
    category: 'Statutory & Tax Registrations',
    desc: 'Mandatory 10-digit code issued by DGFT for cross-border logistics',
    patterns: ['iec', 'import export code', 'import_export_code', 'ieccopy', 'iec copy'],
    isAgentOnly: false,
  },
  {
    key: 'PAN CARD',
    label: 'PAN CARD',
    title: 'Company PAN Card',
    category: 'Statutory & Tax Registrations',
    desc: 'Permanent Account Number copy of entity',
    patterns: ['pan card', 'pancard', 'pan', 'pan copy'],
    isAgentOnly: false,
  },
  {
    key: 'GST COPY',
    label: 'GST COPY',
    title: 'GST Registration Certificate',
    category: 'Statutory & Tax Registrations',
    desc: 'Form GST REG-06 or official GST registration certificate',
    patterns: ['gst copy', 'gstcopy', 'gst certificate', 'gst', 'gstin'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of PAN Allotment',
    label: 'Copy of PAN Allotment',
    title: 'Copy of PAN Allotment',
    category: 'Statutory & Tax Registrations',
    desc: 'PAN Allotment Letter / NSDL / UTI allotment copy',
    patterns: ['copy of pan allotment', 'pan allotment', 'pan allotment letter', 'pan_allotment', 'pan allotment copy'],
    isAgentOnly: false,
  },

  // 2. Constitutional & Legal Documents
  {
    key: 'Certificate of Incorporation',
    label: 'Certificate of Incorporation',
    title: 'Certificate of Incorporation (COI)',
    category: 'Constitutional & Legal Documents',
    desc: 'MCA Certificate of Incorporation / Partnership Deed / Registration',
    patterns: ['certificate of incorporation', 'coi', 'incorporation certificate', 'registration certificate', 'partnership deed'],
    isAgentOnly: false,
  },
  {
    key: 'Memorandum of Association',
    label: 'Memorandum of Association',
    title: 'Memorandum of Association (MOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Memorandum of Association',
    patterns: ['memorandum of association', 'moa'],
    isAgentOnly: false,
  },
  {
    key: 'Articles of Association',
    label: 'Articles of Association',
    title: 'Articles of Association (AOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Articles of Association',
    patterns: ['articles of association', 'aoa'],
    isAgentOnly: false,
  },
  {
    key: 'Power of Attorney',
    label: 'Power of Attorney',
    title: 'Power of Attorney (POA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Power of Attorney or Board Resolution in favor of authorized signatory',
    patterns: ['power of attorney', 'poa', 'board resolution', 'authority letter'],
    isAgentOnly: false,
  },

  // 3. Authorizations, Identification & Address Proof
  {
    key: 'CHA Authority Letter',
    label: 'CHA Authority Letter',
    title: 'CHA Authority Letter',
    category: 'Authorizations & Address Proof',
    desc: 'Customs Broker authorization issued on company letterhead',
    patterns: ['cha authority letter', 'cha authority', 'cha letter', 'cha_authority_letter'],
    isAgentOnly: false,
  },
  {
    key: 'KYC Form',
    label: 'KYC Form',
    title: 'KYC Form',
    category: 'Authorizations & Address Proof',
    desc: 'Duly filled, signed & stamped customer onboarding KYC format',
    patterns: ['kyc form', 'kycform', 'kyc'],
    isAgentOnly: false,
  },
  {
    key: 'AADHAR CARD',
    label: 'AADHAR CARD',
    title: 'Aadhar Card',
    category: 'Authorizations & Address Proof',
    desc: 'Identification proof of authorized signatory / director',
    patterns: ['aadhar card', 'aadhaar card', 'aadhar', 'aadhaar', 'adhar card', 'adhar'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of Telephone Bill',
    label: 'Copy of Telephone Bill',
    title: 'Copy of Telephone Bill',
    category: 'Authorizations & Address Proof',
    desc: 'Telephone / Electricity / Utility bill as registered office address proof',
    patterns: ['copy of telephone bill', 'telephone bill', 'phone bill', 'utility bill', 'electricity bill', 'telephone_bill'],
    isAgentOnly: false,
  },
  {
    key: 'IF AGENT AGREEMENT COPY',
    label: 'IF AGENT AGREEMENT COPY',
    title: 'Agent Agreement Copy',
    category: 'Agency Representation',
    desc: 'Executed agency or representation contract agreement',
    patterns: ['agent agreement', 'agent agreement copy', 'if agent agreement copy', 'agreement copy', 'agent_agreement_copy'],
    isAgentOnly: true,
  },
];

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan',
  'Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia',
  'Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica',
  'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt',
  'El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon',
  'Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel',
  'Italy','Ivory Coast','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar',
  'Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia',
  'Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal',
  'Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan',
  'Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar',
  'Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa',
  'South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan',
  'Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan',
  'Tuvalu','Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City',
  'Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
].sort();

const AddCustomer = () => {
  const navigate = useNavigate();
  const { id: customerId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!customerId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referenceUsers, setReferenceUsers] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [formData, setFormData] = useState({
    business_name: '',
    customer_type: 'Company',
    tax_preference: '',
    cin_number: '',
    pan_number: '',
    reference_by: '',
    remarks: '',
    business_roles: ['SHIPPER'],
    is_active: true
  });

  const [branches, setBranches] = useState([{
    is_head_office: true,
    branch_nick_name: '',
    address_line_1: '',
    address_line_2: '',
    state: '',
    city: '',
    country: 'India',
    pincode: '',
    gst_number: '',
    contact_person: '',
    designation: '',
    contact_numbers: [''],
    emails: [''],
    contacts: [{ contact_person: '', designation: '', phone: '', email: '' }],
    is_active: true
  }]);

  const [kycDocuments, setKycDocuments] = useState([]);
  const [newDocName, setNewDocName] = useState('');
  const [newDocFile, setNewDocFile] = useState(null);
  const [showGstLookup, setShowGstLookup] = useState(false);

  const handleGstSelect = useCallback((record, autoBranches) => {
    const pan = record.pan || panFromGstin(record.gstin || '') || '';
    setFormData(prev => ({
      ...prev,
      business_name: record.legalName || record.tradeName || prev.business_name,
      pan_number: pan || prev.pan_number,
      cin_number: record.cin || record.cinNumber || prev.cin_number || '',
    }));
    setBranches(autoBranches.map(b => ({
      ...b,
      contact_person: '',
      designation: '',
      contact_numbers: [''],
      emails: [''],
      contacts: [{ contact_person: '', designation: '', phone: '', email: '' }],
    })));
    setShowGstLookup(false);
    const headBranch = autoBranches[0] || {};
    toast({
      title: 'GST data applied',
      description: `${autoBranches.length} branch${autoBranches.length > 1 ? 'es' : ''} filled. Head office: ${headBranch.city || headBranch.state || 'address populated'}.`,
    });
  }, [toast]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const getAuthHeadersFormData = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch reference users for dropdown
  const fetchReferenceUsers = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/customers/reference-users/list`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setReferenceUsers(data);
      }
    } catch (error) {
      console.error('Error fetching reference users:', error);
    }
  }, [backendUrl, getAuthHeaders]);

  // Check Drive connection
  const checkDriveStatus = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/drive/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDriveConnected(data.connected);
      }
    } catch (error) {
      console.error('Error checking drive status:', error);
    }
  }, [backendUrl, getAuthHeaders]);

  // Fetch customer data in edit mode
  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/customers/${customerId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Customer not found');
      }
      
      const data = await response.json();
      
      setFormData({
        business_name: data.business_name || '',
        customer_type: data.customer_type || 'Company',
        tax_preference: data.tax_preference || '',
        cin_number: data.cin_number || '',
        pan_number: data.pan_number || '',
        reference_by: data.reference_by || '',
        remarks: data.remarks || '',
        business_roles: data.business_roles && data.business_roles.length > 0 ? data.business_roles : ['SHIPPER'],
        is_active: data.is_active
      });
      
      if (data.branches && data.branches.length > 0) {
        setBranches(data.branches.map(b => ({
          id: b.id,
          is_head_office: b.is_head_office,
          branch_nick_name: b.branch_nick_name,
          address_line_1: b.address_line_1 || '',
          address_line_2: b.address_line_2 || '',
          state: b.state || '',
          city: b.city || '',
          country: b.country || 'India',
          pincode: b.pincode || '',
          gst_number: b.gst_number || '',
          contact_person: b.contact_person,
          designation: b.designation,
          contact_numbers: b.contact_numbers.length > 0 ? b.contact_numbers : [''],
          emails: b.emails.length > 0 ? b.emails : [''],
          // Reconstruct contacts array from legacy flat fields
          contacts: (() => {
            const phones = b.contact_numbers.length > 0 ? b.contact_numbers : [''];
            const emails = b.emails.length > 0 ? b.emails : [''];
            const len = Math.max(phones.length, emails.length, 1);
            return Array.from({ length: len }, (_, i) => ({
              contact_person: i === 0 ? (b.contact_person || '') : '',
              designation: i === 0 ? (b.designation || '') : '',
              phone: phones[i] || '',
              email: emails[i] || '',
            }));
          })(),
          is_active: b.is_active
        })));
      }
      
      if (data.kyc_documents) {
        setKycDocuments(data.kyc_documents);
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast({
        title: "Error",
        description: "Failed to load customer data",
        variant: "destructive"
      });
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  }, [customerId, backendUrl, getAuthHeaders, toast, navigate]);

  useEffect(() => {
    fetchReferenceUsers();
    checkDriveStatus();
    if (isEditMode) {
      fetchCustomer();
    }
  }, [fetchReferenceUsers, checkDriveStatus, fetchCustomer, isEditMode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // When switching to Foreign Party, clear country on all branches (don't default to India)
    if (field === 'customer_type' && value === 'Foreign Party') {
      setBranches(prev => prev.map(b => ({ ...b, country: '' })));
    }
    // When switching back to Company/Individual, default branch country to India if blank
    if (field === 'customer_type' && value !== 'Foreign Party') {
      setBranches(prev => prev.map(b => ({ ...b, country: b.country || 'India' })));
    }
  };

  const handleBranchChange = (index, field, value) => {
    setBranches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleBranchArrayField = (branchIndex, field, itemIndex, value) => {
    setBranches(prev => {
      const updated = [...prev];
      const arr = [...updated[branchIndex][field]];
      arr[itemIndex] = value;
      updated[branchIndex] = { ...updated[branchIndex], [field]: arr };
      return updated;
    });
  };

  const addArrayItem = (branchIndex, field) => {
    setBranches(prev => {
      const updated = [...prev];
      updated[branchIndex] = {
        ...updated[branchIndex],
        [field]: [...updated[branchIndex][field], '']
      };
      return updated;
    });
  };

  const removeArrayItem = (branchIndex, field, itemIndex) => {
    setBranches(prev => {
      const updated = [...prev];
      const arr = updated[branchIndex][field].filter((_, i) => i !== itemIndex);
      updated[branchIndex] = { ...updated[branchIndex], [field]: arr.length > 0 ? arr : [''] };
      return updated;
    });
  };

  // Contact row helpers (new multi-contact UI)
  const handleContactChange = (branchIndex, contactIndex, field, value) => {
    setBranches(prev => {
      const updated = [...prev];
      const contacts = [...updated[branchIndex].contacts];
      contacts[contactIndex] = { ...contacts[contactIndex], [field]: value };
      updated[branchIndex] = { ...updated[branchIndex], contacts };
      return updated;
    });
  };

  const addContact = (branchIndex) => {
    setBranches(prev => {
      const updated = [...prev];
      updated[branchIndex] = {
        ...updated[branchIndex],
        contacts: [...updated[branchIndex].contacts, { contact_person: '', designation: '', phone: '', email: '' }]
      };
      return updated;
    });
  };

  const removeContact = (branchIndex, contactIndex) => {
    setBranches(prev => {
      const updated = [...prev];
      const contacts = updated[branchIndex].contacts.filter((_, i) => i !== contactIndex);
      updated[branchIndex] = { ...updated[branchIndex], contacts: contacts.length > 0 ? contacts : [{ contact_person: '', designation: '', phone: '', email: '' }] };
      return updated;
    });
  };

  const addBranch = () => {
    const defaultCountry = formData.customer_type === 'Foreign Party' ? '' : 'India';
    setBranches(prev => [...prev, {
      is_head_office: false,
      branch_nick_name: '',
      address_line_1: '',
      address_line_2: '',
      state: '',
      city: '',
      country: defaultCountry,
      pincode: '',
      gst_number: '',
      contact_person: '',
      designation: '',
      contact_numbers: [''],
      emails: [''],
      contacts: [{ contact_person: '', designation: '', phone: '', email: '' }],
      is_active: true
    }]);
  };

  const removeBranch = (index) => {
    if (branches.length <= 1) {
      toast({
        title: "Error",
        description: "Customer must have at least one branch",
        variant: "destructive"
      });
      return;
    }
    
    setBranches(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Ensure at least one head office
      if (!updated.some(b => b.is_head_office)) {
        updated[0].is_head_office = true;
      }
      return updated;
    });
  };

  const setAsHeadOffice = (index) => {
    setBranches(prev => prev.map((b, i) => ({
      ...b,
      is_head_office: i === index
    })));
  };

  const handleUploadSpecificDoc = async (docName, file) => {
    if (!file) return;
    if (!customerId) {
      toast({
        title: "Info",
        description: "Please save the customer first before uploading documents",
        variant: "default"
      });
      return;
    }

    try {
      setUploadingDoc(docName);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(
        `${backendUrl}/api/customers/${customerId}/kyc-documents?document_name=${encodeURIComponent(docName)}`,
        {
          method: 'POST',
          headers: getAuthHeadersFormData(),
          body: formDataUpload
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload document');
      }

      const newDoc = await response.json();
      setKycDocuments(prev => [...prev.filter(d => d.id !== newDoc.id), newDoc]);
      
      toast({
        title: "Success",
        description: `${docName} uploaded successfully`
      });
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload document",
        variant: "destructive"
      });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!newDocName || !newDocFile) {
      toast({
        title: "Error",
        description: "Please enter document name and select a file",
        variant: "destructive"
      });
      return;
    }

    if (!customerId) {
      toast({
        title: "Info",
        description: "Please save the customer first before uploading documents",
        variant: "default"
      });
      return;
    }

    try {
      setUploadingDoc(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', newDocFile);

      const response = await fetch(
        `${backendUrl}/api/customers/${customerId}/kyc-documents?document_name=${encodeURIComponent(newDocName)}`,
        {
          method: 'POST',
          headers: getAuthHeadersFormData(),
          body: formDataUpload
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload document');
      }

      const newDoc = await response.json();
      setKycDocuments(prev => [...prev, newDoc]);
      setNewDocName('');
      setNewDocFile(null);
      
      toast({
        title: "Success",
        description: "Document uploaded successfully"
      });
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload document",
        variant: "destructive"
      });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const response = await fetch(
        `${backendUrl}/api/customers/${customerId}/kyc-documents/${docId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setKycDocuments(prev => prev.filter(d => d.id !== docId));
      toast({
        title: "Success",
        description: "Document deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive"
      });
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const token = localStorage.getItem('auth_token');
      window.open(`${backendUrl}/api/drive/files/${doc.drive_file_id}/download?token=${token}`, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const validateForm = () => {
    if (!formData.business_name.trim()) {
      toast({ title: "Validation Error", description: "Business name is required", variant: "destructive" });
      return false;
    }

    if (!formData.customer_type) {
      toast({ title: "Validation Error", description: "Customer type is required", variant: "destructive" });
      return false;
    }

    if (formData.customer_type === 'Company' && !formData.tax_preference) {
      toast({ title: "Validation Error", description: "Tax preference is required for Company customers", variant: "destructive" });
      return false;
    }

    for (let i = 0; i < branches.length; i++) {
      const branch = branches[i];
      if (!branch.branch_nick_name.trim()) {
        toast({
          title: "Validation Error",
          description: `Branch ${i + 1}: Branch nick name is required`,
          variant: "destructive"
        });
        return false;
      }
      if (!branch.address_line_1.trim()) {
        toast({
          title: "Validation Error",
          description: `Branch ${i + 1}: Address line 1 is required`,
          variant: "destructive"
        });
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Helper: convert FastAPI detail (string or array) to readable message
    const parseApiError = (detail) => {
      if (!detail) return null;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      return JSON.stringify(detail);
    };

    try {
      setSaving(true);

      // Prepare branch data — transform contacts array back to API format
      const branchesData = branches.map(b => {
        const contacts = b.contacts || [];
        return {
          ...b,
          contact_person: contacts[0]?.contact_person?.trim() || '',
          designation: contacts[0]?.designation?.trim() || '',
          contact_numbers: contacts.map(c => c.phone).filter(p => p && p.trim()),
          emails: contacts.map(c => c.email).filter(e => e && e.trim()),
        };
      });

      const payload = {
        ...formData,
        tax_preference: formData.tax_preference || null,
        cin_number: formData.cin_number || null,
        pan_number: formData.pan_number || null,
        reference_by: formData.reference_by || null,
        branches: branchesData
      };

      let response;
      if (isEditMode) {
        // Update customer fields
        response = await fetch(`${backendUrl}/api/customers/${customerId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            business_name: formData.business_name,
            customer_type: formData.customer_type,
            tax_preference: formData.tax_preference || null,
            cin_number: formData.cin_number || null,
            pan_number: formData.pan_number || null,
            reference_by: formData.reference_by || null,
            remarks: formData.remarks || null,
            business_roles: formData.business_roles || ['SHIPPER'],
            is_active: formData.is_active
          })
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(parseApiError(errBody.detail) || 'Failed to update customer');
        }

        // Save each branch — create new ones, update existing ones
        for (const branch of branchesData) {
          const branchPayload = {
            is_head_office: branch.is_head_office,
            branch_nick_name: branch.branch_nick_name,
            address_line_1: branch.address_line_1,
            address_line_2: branch.address_line_2,
            state: branch.state,
            city: branch.city,
            country: branch.country,
            pincode: branch.pincode,
            gst_number: branch.gst_number || null,
            contact_person: branch.contact_person || '',
            designation: branch.designation || '',
            contact_numbers: branch.contact_numbers,
            emails: branch.emails,
            is_active: branch.is_active
          };

          let branchRes;
          if (branch.id) {
            branchRes = await fetch(`${backendUrl}/api/customers/${customerId}/branches/${branch.id}`, {
              method: 'PUT',
              headers: getAuthHeaders(),
              body: JSON.stringify(branchPayload)
            });
          } else {
            branchRes = await fetch(`${backendUrl}/api/customers/${customerId}/branches`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify(branchPayload)
            });
          }

          if (!branchRes.ok) {
            const errBody = await branchRes.json().catch(() => ({}));
            throw new Error(parseApiError(errBody.detail) || `Failed to save branch: ${branch.branch_nick_name}`);
          }
        }
      } else {
        // Create customer with branches
        response = await fetch(`${backendUrl}/api/customers`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(parseApiError(errBody.detail) || 'Failed to save customer');
      }

      const savedCustomer = await response.json();

      toast({
        title: "Success",
        description: isEditMode ? "Customer updated successfully" : "Customer created successfully"
      });

      if (!isEditMode) {
        // Navigate to edit mode to allow document uploads
        navigate(`/customers/edit/${savedCustomer.id}`);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save customer",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Andaman and Nicobar Islands", "Lakshadweep"
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
        <span className="ml-2 text-text-secondary font-ibm">Loading customer data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Home</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <Users className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary cursor-pointer hover:text-accent-blue" onClick={() => navigate('/customers')}>Customer</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-accent-blue font-medium">{isEditMode ? 'Edit Customer' : 'Add Customer'}</span>
      </div>

      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-dark font-manrope">
          {isEditMode ? 'Edit Customer' : 'Add Customer'}
        </h1>
      </div>

      {/* Customer Details Section */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-green px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-dark font-manrope">CUSTOMER DETAILS</h2>
          <Button
            type="button"
            data-testid="customer-gst-lookup-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowGstLookup(v => !v)}
            className="text-accent-blue border-accent-blue/30 hover:bg-accent-blue/5 font-ibm text-xs font-semibold gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {showGstLookup ? 'Close GST Lookup' : 'Verify GST / PAN (Attestr)'}
          </Button>
        </div>

        {/* GST Lookup Panel (inline) */}
        {showGstLookup && (
          <div className="px-6 pt-4 pb-2">
            <GstLookup
              initialName={formData.business_name}
              onSelect={handleGstSelect}
              onClose={() => setShowGstLookup(false)}
            />
          </div>
        )}
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Business Name */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Business Name<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="customer-business-name-input"
                placeholder="Customer / Business Name"
                value={formData.business_name}
                onChange={(e) => handleInputChange('business_name', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* Customer Type */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Customer Type<span className="text-accent-red ml-1">*</span>
              </Label>
              <SearchableSelect
                data-testid="customer-type-select"
                value={formData.customer_type}
                onValueChange={(val) => handleInputChange('customer_type', val)}
                placeholder="--Select Type--"
                searchPlaceholder="Search type..."
                options={[
                  { value: 'Company', label: 'Company' },
                  { value: 'Individual', label: 'Individual' },
                  { value: 'Foreign Party', label: 'Foreign Party' },
                ]}
              />
            </div>

            {/* Tax Preference — compulsory only for Company */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Tax Preference
                {formData.customer_type === 'Company' && <span className="text-accent-red ml-1">*</span>}
              </Label>
              <SearchableSelect
                data-testid="tax-preference-select"
                value={formData.tax_preference || ''}
                onValueChange={(val) => handleInputChange('tax_preference', val)}
                placeholder="--Select--"
                searchPlaceholder="Search preference..."
                options={[
                  { value: 'NONE', label: 'NONE' },
                  { value: 'SALES EXEMPTION', label: 'SALES EXEMPTION' },
                  { value: 'SALES IGST 12', label: 'SALES IGST 12' },
                  { value: 'SALES IGST 18', label: 'SALES IGST 18' },
                  { value: 'SALES LOCAL 12', label: 'SALES LOCAL 12' },
                  { value: 'SALES LOCAL 18', label: 'SALES LOCAL 18' },
                  { value: 'SALES REIMBERSEMENT', label: 'SALES REIMBERSEMENT' },
                  { value: 'SALES SEZ', label: 'SALES SEZ' },
                ]}
              />
            </div>

            {/* CIN Number */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">CIN Number</Label>
              <Input
                data-testid="cin-number-input"
                placeholder="CIN Number (optional)"
                value={formData.cin_number}
                onChange={(e) => handleInputChange('cin_number', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* PAN Number */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">PAN Number</Label>
              <Input
                data-testid="pan-number-input"
                placeholder="PAN Number"
                value={formData.pan_number}
                onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm uppercase"
                maxLength={10}
              />
            </div>

            {/* Reference By */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Reference By (Employee)</Label>
              <SearchableSelect
                data-testid="reference-by-select"
                value={formData.reference_by || ''}
                onValueChange={(val) => handleInputChange('reference_by', val)}
                placeholder="-Select Employee-"
                searchPlaceholder="Search employee..."
                options={[
                  { value: '', label: 'None' },
                  ...referenceUsers.map(user => ({
                    value: user.id,
                    label: `${user.full_name}${user.designation ? ` (${user.designation})` : ''}`
                  }))
                ]}
              />
            </div>

            {/* Active Status */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Status</Label>
              <SearchableSelect
                data-testid="status-select"
                value={formData.is_active ? 'active' : 'inactive'}
                onValueChange={(val) => handleInputChange('is_active', val === 'active')}
                placeholder="Select Status"
                searchPlaceholder="Search status..."
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
              />
            </div>

            {/* Remarks */}
            <div className="space-y-2 lg:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Remarks</Label>
              <Textarea
                data-testid="remarks-textarea"
                placeholder="Remarks"
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm min-h-[80px]"
              />
            </div>

            {/* Business Roles Bifurcation */}
            <div className="space-y-2 lg:col-span-2 p-3.5 bg-gradient-to-r from-emerald-50/50 to-teal-50/40 rounded-xl border border-emerald-100">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5 text-emerald-900">
                  <span>Business Roles (Bifurcation)</span>
                  <span className="text-[10px] text-emerald-600 font-normal lowercase">(Multi-select applicable roles)</span>
                </Label>
                {formData.business_roles?.includes('AGENT') && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shadow-2xs">
                    ⚡ Auto-syncs to Agent Master
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 pt-1">
                {[
                  { key: 'SHIPPER', label: 'Shipper / Exporter', desc: 'Direct cargo / shipper' },
                  { key: 'CONSIGNEE', label: 'Consignee / Importer', desc: 'Destination receiver / buyer' },
                  { key: 'FORWARDER', label: 'Freight Forwarder', desc: 'Co-loader / intermediary' },
                  { key: 'AGENT', label: 'Agent', desc: 'IATA / destination agent' }
                ].map((role) => {
                  const isChecked = formData.business_roles?.includes(role.key);
                  return (
                    <label
                      key={role.key}
                      className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-all border ${
                        isChecked
                          ? 'bg-white border-emerald-500 shadow-2xs ring-1 ring-emerald-200'
                          : 'bg-white/60 border-gray-200 hover:bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const currentRoles = Array.isArray(formData.business_roles) ? [...formData.business_roles] : ['SHIPPER'];
                          let updated;
                          if (e.target.checked) {
                            updated = Array.from(new Set([...currentRoles, role.key]));
                          } else {
                            updated = currentRoles.filter(r => r !== role.key);
                            if (updated.length === 0) updated = ['SHIPPER'];
                          }
                          handleInputChange('business_roles', updated);
                        }}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 mt-0.5"
                      />
                      <div className="min-w-0">
                        <div className={`text-xs font-bold leading-tight ${isChecked ? 'text-emerald-900' : 'text-slate-700'}`}>
                          {role.label}
                        </div>
                        <div className="text-[10px] text-slate-500 leading-tight truncate">
                          {role.desc}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Branches Section */}
      {branches.map((branch, branchIndex) => (
        <Card key={branchIndex} className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="border-b-4 border-accent-green px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-accent-green" />
              <h2 className="text-lg font-semibold text-text-dark font-manrope">
                {branch.branch_nick_name
                  ? branch.branch_nick_name
                  : (branch.is_head_office ? 'Head Office' : `Branch ${branchIndex + 1}`)}
              </h2>
              {!branch.is_head_office && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAsHeadOffice(branchIndex)}
                  className="text-xs"
                >
                  Set as Head Office
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {branchIndex === branches.length - 1 && (
                <Button
                  data-testid="add-branch-button"
                  type="button"
                  onClick={addBranch}
                  className="bg-accent-cyan hover:bg-accent-cyan/90 text-white font-ibm font-semibold h-9 px-4 rounded-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Branch
                </Button>
              )}
              {branches.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => removeBranch(branchIndex)}
                  className="text-accent-red hover:text-accent-red hover:bg-accent-red/10 h-9"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Branch Nick Name */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Branch Nick Name<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`branch-${branchIndex}-name-input`}
                  placeholder="Branch Nick Name"
                  value={branch.branch_nick_name}
                  onChange={(e) => handleBranchChange(branchIndex, 'branch_nick_name', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* Address Line 1 */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Address Line 1<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`branch-${branchIndex}-address1-input`}
                  placeholder="Address Line 1"
                  value={branch.address_line_1}
                  onChange={(e) => handleBranchChange(branchIndex, 'address_line_1', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* Address Line 2 */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Address Line 2</Label>
                <Input
                  data-testid={`branch-${branchIndex}-address2-input`}
                  placeholder="Address Line 2"
                  value={branch.address_line_2}
                  onChange={(e) => handleBranchChange(branchIndex, 'address_line_2', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* State */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">State / Province</Label>
                {(!branch.country || branch.country === 'India') ? (
                  <SearchableSelect
                    data-testid={`branch-${branchIndex}-state-select`}
                    value={branch.state || ''}
                    onValueChange={(val) => handleBranchChange(branchIndex, 'state', val)}
                    placeholder="-Select State-"
                    searchPlaceholder="Search state..."
                    options={[
                      { value: '', label: '-Select State-' },
                      ...indianStates.map(s => ({ value: s, label: s }))
                    ]}
                  />
                ) : (
                  <Input
                    data-testid={`branch-${branchIndex}-state-select`}
                    placeholder="State / Province / Region"
                    value={branch.state}
                    onChange={(e) => handleBranchChange(branchIndex, 'state', e.target.value)}
                    className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  />
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">City</Label>
                <Input
                  data-testid={`branch-${branchIndex}-city-input`}
                  placeholder="City"
                  value={branch.city}
                  onChange={(e) => handleBranchChange(branchIndex, 'city', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Country</Label>
                <SearchableSelect
                  data-testid={`branch-${branchIndex}-country-input`}
                  value={branch.country || ''}
                  onValueChange={(val) => {
                    handleBranchChange(branchIndex, 'country', val);
                    if (val !== 'India') handleBranchChange(branchIndex, 'state', '');
                  }}
                  placeholder="Select Country"
                  searchPlaceholder="Search country..."
                  options={[
                    { value: '', label: '-- Select Country --' },
                    ...COUNTRIES.map(c => ({ value: c, label: c }))
                  ]}
                />
              </div>

              {/* Pincode */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Pincode</Label>
                <Input
                  data-testid={`branch-${branchIndex}-pincode-input`}
                  placeholder="Pincode"
                  value={branch.pincode}
                  onChange={(e) => handleBranchChange(branchIndex, 'pincode', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  maxLength={6}
                />
              </div>

              {/* GST Number */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">GST Number</Label>
                <Input
                  data-testid={`branch-${branchIndex}-gst-input`}
                  placeholder="GST Number"
                  value={branch.gst_number}
                  onChange={(e) => handleBranchChange(branchIndex, 'gst_number', e.target.value.toUpperCase())}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm uppercase"
                  maxLength={15}
                />
              </div>

              {/* Contacts — each row: Contact Person | Designation | Phone | Email | [+] [x] */}
              <div className="space-y-2 col-span-2 lg:col-span-4">
                <Label className="text-text-dark font-ibm font-medium">Contacts</Label>
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 mb-1">
                  {['Contact Person', 'Designation', 'Phone', 'Email'].map(h => (
                    <span key={h} className="text-xs text-text-secondary font-ibm font-medium px-1">{h}</span>
                  ))}
                  <span />
                </div>
                {(branch.contacts || []).map((contact, ci) => (
                  <div key={ci} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                    <Input
                      data-testid={`branch-${branchIndex}-contact-${ci}-person`}
                      placeholder="Contact Person"
                      value={contact.contact_person}
                      onChange={e => handleContactChange(branchIndex, ci, 'contact_person', e.target.value)}
                      className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                    />
                    <Input
                      data-testid={`branch-${branchIndex}-contact-${ci}-designation`}
                      placeholder="Designation"
                      value={contact.designation}
                      onChange={e => handleContactChange(branchIndex, ci, 'designation', e.target.value)}
                      className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                    />
                    <Input
                      data-testid={`branch-${branchIndex}-contact-${ci}-phone`}
                      placeholder="Phone Number"
                      value={contact.phone}
                      onChange={e => handleContactChange(branchIndex, ci, 'phone', e.target.value)}
                      className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                    />
                    <Input
                      data-testid={`branch-${branchIndex}-contact-${ci}-email`}
                      placeholder="Email"
                      type="email"
                      value={contact.email}
                      onChange={e => handleContactChange(branchIndex, ci, 'email', e.target.value)}
                      className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                    />
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => addContact(branchIndex)}
                        className="shrink-0 bg-accent-orange hover:bg-accent-orange/90 text-white border-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      {(branch.contacts || []).length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeContact(branchIndex, ci)}
                          className="shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {/* KYC Documents Section */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" id="kyc">
        {(() => {
          const isAgent = (formData.business_roles || []).some(r => String(r).toUpperCase() === 'AGENT');
          const activeRequiredDocs = REQUIRED_KYC_DOCS.filter(d => !d.isAgentOnly || isAgent);
          
          const getUploadedDoc = (docConfig) => {
            return kycDocuments.find(d => {
              const name = (d.document_name || '').trim().toLowerCase();
              return docConfig.patterns.some(p => name === p || name.includes(p));
            });
          };

          const completedCount = activeRequiredDocs.filter(d => !!getUploadedDoc(d)).length;
          const totalRequired = activeRequiredDocs.length;
          const isFullyVerified = totalRequired > 0 && completedCount === totalRequired;
          const progressPercent = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;

          // Other custom uploaded documents (not matching standard checklist)
          const customDocs = kycDocuments.filter(d => {
            const name = (d.document_name || '').trim().toLowerCase();
            return !REQUIRED_KYC_DOCS.some(rd => rd.patterns.some(p => name === p || name.includes(p)));
          });

          return (
            <div>
              <div className="border-b-4 border-accent-green px-6 py-4 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-text-dark font-manrope flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-accent-green" />
                    KYC Compliance & Verification Documents
                  </h2>
                  <p className="text-xs text-text-secondary font-ibm mt-0.5">
                    Mandatory statutory documents required for compliance onboarding
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {isFullyVerified ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      KYC Verified ({completedCount}/{totalRequired})
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      KYC Pending ({completedCount}/{totalRequired})
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="px-6 pt-4 pb-2">
                <div className="flex justify-between items-center text-xs text-text-secondary font-ibm mb-1.5">
                  <span>Document Completion: {completedCount} of {totalRequired} documents verified</span>
                  <span className="font-semibold text-text-dark">{progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${isFullyVerified ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              <div className="p-6 space-y-6">
                {!driveConnected && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-center justify-between">
                    <span>Local Document Vault is active. Documents will be stored securely on the server.</span>
                  </div>
                )}

                {!isEditMode && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <span>Please save customer details first to unlock and upload the KYC document checklist.</span>
                  </div>
                )}

                {/* Structured KYC Checklist Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeRequiredDocs.map((docConfig) => {
                    const uploaded = getUploadedDoc(docConfig);
                    const isUploadingThis = uploadingDoc === docConfig.key;

                    return (
                      <div
                        key={docConfig.key}
                        className={`rounded-xl border p-4 flex flex-col justify-between transition-all ${
                          uploaded
                            ? 'bg-emerald-50/40 border-emerald-200'
                            : 'bg-white border-amber-200/80 hover:border-amber-400 border-dashed'
                        }`}
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <span className="font-semibold text-sm text-text-dark font-ibm flex items-center gap-1.5">
                              {uploaded ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                              ) : (
                                <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                              )}
                              {docConfig.title}
                            </span>
                            {uploaded ? (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase tracking-wider shrink-0">
                                Verified
                              </span>
                            ) : (
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wider shrink-0">
                                As Applicable
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-text-secondary font-ibm mb-3 leading-relaxed">
                            {docConfig.desc}
                          </p>
                        </div>

                        {uploaded ? (
                          <div className="pt-2 border-t border-emerald-100/80 flex items-center justify-between text-xs font-ibm">
                            <span className="truncate max-w-[150px] text-text-dark font-medium" title={uploaded.file_name}>
                              {uploaded.file_name}
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(uploaded)}
                                className="h-7 px-2 text-text-dark hover:text-accent-blue"
                                title="Download Document"
                              >
                                <Download className="w-3.5 h-3.5 mr-1" />
                                View
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDocument(uploaded.id)}
                                className="h-7 px-2 text-accent-red hover:bg-red-50"
                                title="Delete & Re-upload"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="pt-2 border-t border-gray-100">
                            <input
                              type="file"
                              id={`upload-${docConfig.key}`}
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleUploadSpecificDoc(docConfig.key, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                              disabled={!isEditMode || isUploadingThis}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!isEditMode || isUploadingThis}
                              onClick={() => {
                                const input = document.getElementById(`upload-${docConfig.key}`);
                                if (input) input.click();
                              }}
                              className="w-full text-xs font-ibm font-medium bg-amber-50 hover:bg-amber-100 text-amber-900 border-amber-300"
                            >
                              {isUploadingThis ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                                  Upload {docConfig.label}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Custom / Additional Uploaded Documents */}
                {customDocs.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-text-dark font-manrope mb-3">
                      Additional Documents ({customDocs.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {customDocs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <p className="font-medium text-text-dark font-ibm text-sm">{doc.document_name}</p>
                            <p className="text-xs text-text-secondary">{doc.file_name}</p>
                          </div>
                          <div className="flex gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadDocument(doc)}
                              className="h-8 text-xs font-ibm"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" />
                              Download
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="h-8 text-xs font-ibm text-accent-red hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Custom / Additional Document */}
                {isEditMode && (
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary font-ibm mb-3">
                      Upload Other / Additional Document
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-text-dark font-ibm font-medium">Document Description</Label>
                        <Input
                          data-testid="document-name-input"
                          placeholder="e.g., Factory License, Power of Attorney"
                          value={newDocName}
                          onChange={(e) => setNewDocName(e.target.value)}
                          className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm h-9 text-xs"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-text-dark font-ibm font-medium">Select File</Label>
                        <Input
                          type="file"
                          data-testid="document-upload-input"
                          onChange={(e) => setNewDocFile(e.target.files[0])}
                          className="border-gray-200 focus:border-accent-blue font-ibm h-9 text-xs"
                        />
                      </div>

                      <div className="flex items-end">
                        <Button
                          data-testid="upload-document-button"
                          type="button"
                          onClick={handleUploadDocument}
                          disabled={uploadingDoc || !newDocName || !newDocFile}
                          className="w-full bg-accent-orange hover:bg-accent-orange/90 text-white font-ibm font-semibold h-9 rounded-lg text-xs"
                        >
                          {uploadingDoc ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-3.5 h-3.5 mr-1.5" />
                              Upload Additional File
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4">
        <Button
          data-testid="cancel-button"
          variant="outline"
          onClick={() => navigate('/customers')}
          className="bg-gray-100 hover:bg-gray-200 text-text-dark border-0 font-ibm font-semibold h-11 px-8 rounded-lg"
        >
          Cancel
        </Button>
        <Button
          data-testid="save-customer-button"
          onClick={handleSave}
          disabled={saving}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-8 rounded-lg"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            isEditMode ? 'Update Customer' : 'Save Customer'
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddCustomer;
